import React, { createContext, useCallback, useContext, useState } from 'react';
import { X, AlertCircle, CheckCircle2, Info } from 'lucide-react';

/**
 * Replaces window.alert() as the app's error/status surface. Native alert() blocks the
 * thread and looks nothing like the rest of the UI; this renders in-flow, dismisses itself,
 * and is announced to screen readers via aria-live instead of a modal browser dialog.
 */
const ToastContext = createContext(null);
export const useToast = () => useContext(ToastContext);

let idCounter = 0;

const ICONS = {
    error: <AlertCircle size={18} className="shrink-0" />,
    success: <CheckCircle2 size={18} className="shrink-0" />,
    info: <Info size={18} className="shrink-0" />,
};

const STYLES = {
    error: 'bg-red-600 border-red-700 text-white',
    success: 'bg-emerald-600 border-emerald-700 text-white',
    info: 'bg-gray-900 border-gray-800 text-white',
};

export function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);

    const dismissToast = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    const showToast = useCallback((message, type = 'info', duration = 5000) => {
        const id = ++idCounter;
        setToasts(prev => [...prev, { id, message, type }]);
        if (duration > 0) {
            setTimeout(() => dismissToast(id), duration);
        }
        return id;
    }, [dismissToast]);

    return (
        <ToastContext.Provider value={{ showToast, dismissToast }}>
            {children}
            <div
                className="fixed top-20 md:top-24 left-1/2 -translate-x-1/2 z-[1000] flex flex-col gap-2 w-[92%] max-w-sm pointer-events-none"
                aria-live="polite"
                aria-atomic="true"
            >
                {toasts.map(t => (
                    <div
                        key={t.id}
                        role="alert"
                        className={`pointer-events-auto flex items-start gap-3 px-4 py-3.5 rounded-card shadow-2xl border text-sm font-bold animate-in fade-in slide-in-from-top-4 duration-300 ${STYLES[t.type] || STYLES.info}`}
                    >
                        {ICONS[t.type] || ICONS.info}
                        <span className="flex-1 leading-snug">{t.message}</span>
                        <button
                            onClick={() => dismissToast(t.id)}
                            aria-label="Dismiss notification"
                            className="opacity-70 hover:opacity-100 transition-opacity shrink-0"
                        >
                            <X size={16} />
                        </button>
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    );
}
